import { useParams } from "@tanstack/react-router";
import { Lightbulb, Pencil, Tag } from "lucide-react";
import { useState } from "react";
import { CompetencyFormDialog } from "@/components/competencies/CompetencyFormDialog";
import { CompetencyPeopleSection } from "@/components/competencies/CompetencyPeopleSection";
import { ActivityLog } from "@/components/shared/activity-log";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { type DetailMetaItem, EntityDetailHeader } from "@/components/shared/entity-detail-header";
import { EntityDetailPage } from "@/components/shared/entity-detail-page";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { RequiredByRolesSection } from "@/components/shared/required-by-roles-section";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { Button } from "@/components/ui/button";
import { useCompetency } from "@/hooks/use-competencies";
import { useSession } from "@/lib/auth-client";
import { canEditAll } from "@/lib/permissions";

type TabKey = "overview" | "people" | "history";

const TABS: TabItem[] = [
  { key: "overview", label: "Overview" },
  { key: "people", label: "People" },
  { key: "history", label: "Change history" },
];

export function CompetencyDetailPage() {
  const params = useParams({ strict: false });
  const competencyId = typeof params.competencyId === "string" ? params.competencyId : "";
  const query = useCompetency(competencyId);
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>("overview");

  return (
    <EntityDetailPage
      query={query}
      entityLabel="Competency"
      section="Capability"
      list={{ label: "Competencies", to: "/dashboard/competencies" }}
      name={query.data?.name}
    >
      {(competency) => {
        const categoryLabel = competency.categories.map((c) => c.name).join(", ") || null;
        const meta: DetailMetaItem[] = [];
        if (categoryLabel) meta.push({ key: "category", icon: Tag, node: categoryLabel });

        return (
          <>
            <EntityDetailHeader
              visual={
                <span className="flex size-20 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Lightbulb className="size-8" />
                </span>
              }
              title={competency.name}
              subtitle={competency.description}
              meta={meta}
              badges={<LifecycleBadge status={competency.status} />}
              actions={
                isAdmin && (
                  <Button size="sm" onClick={() => setEditing(true)}>
                    <Pencil className="mr-1.5 size-3.5" />
                    Edit
                  </Button>
                )
              }
            />

            <TabBar tabs={TABS} value={tab} onChange={(k) => setTab(k as TabKey)} />

            {tab === "overview" && (
              <>
                <DetailSection title="Overview">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <MetadataRow
                      label={competency.categories.length === 1 ? "Category" : "Categories"}
                      value={categoryLabel}
                    />
                    <MetadataRow label="Definition" value={competency.description} />
                  </div>
                </DetailSection>
                <DetailSection title="Required by roles">
                  <RequiredByRolesSection
                    roles={competency.roles}
                    originKind="competency"
                    entityId={competency.id}
                    entityName={competency.name}
                  />
                </DetailSection>
              </>
            )}

            {tab === "people" && (
              <DetailSection title="People with this competency">
                <CompetencyPeopleSection
                  competencyId={competency.id}
                  competencyName={competency.name}
                />
              </DetailSection>
            )}

            {tab === "history" && (
              <DetailSection title="Change history">
                <ActivityLog
                  entityType="competency"
                  entityId={competency.id}
                  fieldLabels={{
                    // Historical diff keys from before the category/theme
                    // merge — never rewritten, see .claude/rules/api.md.
                    categoryId: "Category",
                    themes: "Themes",
                    categories: "Categories",
                    // Diff keys stay `level_<n>` — historical rows carry them and are
                    // never rewritten. Only the label follows the WI-103 rename.
                    level_1: "Rating 1",
                    level_2: "Rating 2",
                    level_3: "Rating 3",
                    level_4: "Rating 4",
                    level_5: "Rating 5",
                  }}
                />
              </DetailSection>
            )}

            {isAdmin && (
              <CompetencyFormDialog
                key={competency.updatedAt}
                open={editing}
                onOpenChange={setEditing}
                competency={competency}
              />
            )}
          </>
        );
      }}
    </EntityDetailPage>
  );
}
